import { type Ref, computed, ref } from "@nuxtjs/composition-api";
import { defineStore } from "pinia";

import { type BubbleOptionsMap, type CategoriesStore } from "@/types/apis/categories";
import { cleanHashtag } from "@/utils/transform/text";

import useCategoriesApi from "./-api";

export const useCategoriesStore = defineStore(
  "categories",
  (): CategoriesStore => {
    // State
    const categories: Ref<null | BubbleOptionsMap> = ref(null);
    const categoriesList = ref<string[]>([]);

    // Getters
    const supportedHashtags = computed(() => Object.keys(categories.value ?? {}));

    // Actions -- Don't forget to provide mock implementation or tell pinia to unmock to exercise in tests
    const category = (categoryName: string) => categories.value && categories.value[categoryName.toLowerCase()];

    const mapLocalesForPresentationApi = (locale: string) => {
      switch (locale) {
        case "en-GB":
          return "uk";
        case "zh":
        case "zh-Hant":
          return "cn";
        case "es-ES":
          return "es";
        case "pt":
          return "br";
        case "ko":
          return "kr";
        default:
          return locale;
      }
    };

    return {
      categories,
      categoriesList,
      supportedHashtags,
      category,
      getHashtags: async function getHashtags(locale?: string) {
        try {
          const response = await this.$api.post(
            mapLocalesForPresentationApi(locale ?? this.$store?.$i18n?.locale ?? "us"),
          );
          const cats: BubbleOptionsMap = {};
          categoriesList.value = [];
          response.data.modules.forEach((module) => {
            const keyName = cleanHashtag(module.options.actions[0].feedHashtag ?? module.options.key);
            categoriesList.value.push(keyName);
            cats[keyName] = module.options;
          });
          categories.value = cats;
        } catch (error) {
          console.error("Error fetching hashtags", error);
        }
      },
    };
  },
  {
    api: useCategoriesApi,
  },
);
